import "../../../shared/icons/default.css";
import "../../../shared/app.css";
import "../styles/web-compat.css";
import "./styles.css";
import "../../../shared/ink.js";
import "../../../shared/motion.js";
import "../../../shared/idle.js";
import "../../../shared/feedkeys.js";
// The tooltips (docs/tooltips-plan.md D721), the placement they are put where
// by (D425, D727), the Ctrl mode that holds them up with their keys
// (D736–D746) and the arrows in menus and panels (D760–D763) — the desktop's
// head carries the same four, in order.
import "../../../shared/placement.js";
import "../../../shared/tips.js";
import "../../../shared/keytips.js";
import "../../../shared/menukeys.js";
import "../../../shared/stickers/sticker.js";
import { StrictMode, useEffect, useState, type ComponentType } from "react";
import { createRoot } from "react-dom/client";
import emojiRange from "../../../shared/fonts/echo-emoji.range?raw";
// The sticker player's worker and engine (docs/stickers-plan.md D553), as
// files beside the bundle: `pnpm wasm:sticker` builds the engine.
import stickerWorkerUrl from "../../../shared/stickers/sticker-worker.js?url";
import stickerWasmUrl from "../../../shared/stickers/tlottie.wasm?url";
import { createStartupLoader, initializeApplication } from "./lib/bootstrap";
import { Runtime } from "../store/runtime";
import * as sounds from "../lib/sounds";
import { startStickerPlayer } from "../lib/stickers";
import { swap } from "./lib/transition";

const runtime = new Runtime({ transition: swap });
runtime.onRing = (start) => (start ? sounds.ringStart() : sounds.ringStop());
if (import.meta.env.DEV) (window as unknown as { __rt: Runtime }).__rt = runtime;
startStickerPlayer(stickerWorkerUrl, stickerWasmUrl);
// The emoji face is Apple's artwork, generated by `tools/emoji/gen.py
// --subset` and never committed (docs/reactions-plan.md D1009, D1010). A glob
// rather than an import, so a checkout without it still builds: no face, no
// `@font-face`, and the chips draw with the platform's emoji.
const emojiFontUrl = Object.values(
  import.meta.glob<string>("../../../shared/fonts/echo-emoji.woff2", {
    query: "?url",
    import: "default",
    eager: true,
  }),
)[0];
if (emojiFontUrl && !document.getElementById("echo-emoji-face")) {
  const face = document.createElement("style");
  face.id = "echo-emoji-face";
  // Upright and italic, every weight, one URL: the face has neither, and in
  // italic text (D1011) the browser would otherwise slant the pictures.
  face.textContent = ["normal", "italic"]
    .map(
      (style) =>
        `@font-face{font-family:"Echo Emoji";src:url(${JSON.stringify(emojiFontUrl)}) format("woff2");font-style:${style};font-weight:1 1000;font-display:block;unicode-range:${emojiRange.trim()}}`,
    )
    .join("");
  document.head.appendChild(face);
}
const startup = createStartupLoader(
  async () => {
    await initializeApplication();
    return (await import("./app")).App;
  },
  () => {
    runtime.start();
    runtime.resume();
  },
);
function Startup() {
  const [App, setApp] = useState<ComponentType<{ rt: Runtime }> | null>(null);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    void startup.load().then(
      (component) => {
        if (live) setApp(() => component);
      },
      () => {
        if (live) setError(true);
      },
    );
    return () => {
      live = false;
    };
  }, [attempt]);
  if (App) return <App rt={runtime} />;
  const ru = navigator.language.startsWith("ru");
  return (
    <div className="window">
      <div className="login">
        <div className="login__card echo-material-thick">
          <div className="login__title">EchoFloat</div>
          <p role={error ? "alert" : "status"}>
            {error
              ? ru
                ? "Не удалось загрузить приложение. Проверьте соединение и повторите попытку."
                : "Unable to load the application. Check your connection and try again."
              : ru
                ? "Загрузка…"
                : "Loading…"}
          </p>
          {error && (
            <button
              type="button"
              className="echo-button echo-button--prominent"
              onClick={() => {
                setError(false);
                setAttempt((n) => n + 1);
              }}
            >
              {ru ? "Повторить" : "Retry"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
const element = document.getElementById("main");
if (!element) throw new Error("index.html lost its #main");
const root = createRoot(element);
root.render(
  <StrictMode>
    <Startup />
  </StrictMode>,
);
import.meta.hot?.dispose(() => {
  startup.cancel();
  root.unmount();
  runtime.dispose();
});
